export type WebsiteTrafficSummary = {
  sessions: number;
  pageviews: number;
  activeUsers: number;
  bounceRate: number;
};

export type WebsiteTrafficDeltas = {
  sessions: number | null;
  pageviews: number | null;
  activeUsers: number | null;
  bounceRate: number | null;
};

export type WebsiteTrafficPeriod = WebsiteTrafficSummary & {
  deltas: WebsiteTrafficDeltas;
};

export type WebsiteTrafficTopPage = {
  path: string;
  pageviews: number;
};

export type WebsiteTrafficSource = {
  channel: string;
  sessions: number;
};

export type WebsiteTrafficTrendPoint = {
  date: string;
  sessions: number;
  activeUsers: number;
};

export type WebsiteTrafficSnapshotResponse = {
  asOf: string;
  yesterday: WebsiteTrafficPeriod;
  week: WebsiteTrafficPeriod;
  topPages: WebsiteTrafficTopPage[];
  trafficSources: WebsiteTrafficSource[];
  trend: WebsiteTrafficTrendPoint[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isSummary(value: unknown): value is WebsiteTrafficSummary {
  if (!isRecord(value)) return false;
  return (
    typeof value.sessions === "number" &&
    typeof value.pageviews === "number" &&
    typeof value.activeUsers === "number" &&
    typeof value.bounceRate === "number"
  );
}

function isDeltaValue(value: unknown): value is number | null {
  return value === null || typeof value === "number";
}

function isDeltas(value: unknown): value is WebsiteTrafficDeltas {
  if (!isRecord(value)) return false;
  return (
    isDeltaValue(value.sessions) &&
    isDeltaValue(value.pageviews) &&
    isDeltaValue(value.activeUsers) &&
    isDeltaValue(value.bounceRate)
  );
}

function isPeriod(value: unknown): value is WebsiteTrafficPeriod {
  if (!isRecord(value)) return false;
  if (!isSummary(value)) return false;
  return isDeltas((value as { deltas?: unknown }).deltas);
}

export function isWebsiteTrafficSnapshotResponse(
  value: unknown,
): value is WebsiteTrafficSnapshotResponse {
  if (!isRecord(value)) return false;
  if (typeof value.asOf !== "string") return false;
  if (!isPeriod(value.yesterday) || !isPeriod(value.week)) return false;
  if (!Array.isArray(value.topPages) || !Array.isArray(value.trafficSources)) return false;
  if (!Array.isArray(value.trend)) return false;
  return true;
}
